import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { isNavPathActive, resolveNavSection } from '../tools/catalog';

function revealOpenedSection(element) {
    const scroller = element?.closest('.sidebar-nav-scroll');
    if (!element || !scroller) return;
    const overflow = element.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
    if (overflow > 0) scroller.scrollBy({ top: overflow + 8 });
}

function NavSection({ section, pathname, isCollapsed, expanded, onToggle, onExpand, collapseOnReclick = true, badges = {} }) {
    const sectionRef = useRef(null);
    const revealRef = useRef(false);
    const resolved = resolveNavSection(section);
    const childActive = resolved.children.some(child => isNavPathActive(pathname, child.path));
    const selfActive = Boolean(resolved.path) && isNavPathActive(pathname, resolved.path) && !childActive;
    const showChildren = expanded && !isCollapsed && resolved.children.length > 0;
    const sectionBadge = resolved.children.reduce((sum, child) => sum + (badges[child.path] || 0), 0);
    const collapsedTarget = resolved.path || resolved.children[0]?.path || '/projects';
    const rowClass = [
        'sidebar-nav-row',
        selfActive ? 'is-active' : '',
        childActive ? 'has-active-child' : '',
        isCollapsed ? 'is-collapsed' : ''
    ].filter(Boolean).join(' ');

    useEffect(() => {
        if (!expanded || !revealRef.current) return;
        revealRef.current = false;
        revealOpenedSection(sectionRef.current);
    }, [expanded]);

    const openAndReveal = (action) => {
        if (!expanded) revealRef.current = true;
        action();
    };

    const icon = (
        <i className={resolved.icon} style={{ fontSize: '1.1rem', width: '1.25rem', textAlign: 'center', flexShrink: 0 }} />
    );

    let main;
    if (isCollapsed) {
        main = (
            <Link
                to={collapsedTarget}
                className="sidebar-nav-parent-main"
                title={resolved.label}
                onClick={() => onExpand(resolved.id)}
            >
                {icon}
            </Link>
        );
    } else if (resolved.expandOnly) {
        main = (
            <button
                type="button"
                className="sidebar-nav-parent-main"
                aria-expanded={expanded}
                onClick={() => openAndReveal(() => onToggle(resolved.id))}
            >
                {icon}
                <span className="sidebar-nav-label">{resolved.label}</span>
                {sectionBadge > 0 && !showChildren && (
                    <span className="sidebar-nav-badge" aria-label={`${sectionBadge} pending access requests`}>{sectionBadge}</span>
                )}
                {resolved.children.length > 0 && (
                    <i className={`fa-solid fa-chevron-down sidebar-nav-chevron-icon${expanded ? ' is-open' : ''}`} style={{ marginLeft: sectionBadge > 0 && !showChildren ? '0.4rem' : 'auto' }} aria-hidden="true" />
                )}
            </button>
        );
    } else {
        const hasChildren = resolved.children.length > 0;
        main = (
            <Link
                to={resolved.path}
                className="sidebar-nav-parent-main"
                aria-expanded={hasChildren ? expanded : undefined}
                onClick={(event) => {
                    if (collapseOnReclick && selfActive && expanded) {
                        event.preventDefault();
                        onToggle(resolved.id);
                        return;
                    }
                    openAndReveal(() => onExpand(resolved.id));
                }}
            >
                {icon}
                <span className="sidebar-nav-label">{resolved.label}</span>
                {hasChildren && (
                    <i className={`fa-solid fa-chevron-down sidebar-nav-chevron-icon${expanded ? ' is-open' : ''}`} style={{ marginLeft: 'auto' }} aria-hidden="true" />
                )}
            </Link>
        );
    }

    return (
        <div ref={sectionRef} className="sidebar-nav-section">
            <div className={rowClass}>
                {main}
            </div>
            {showChildren && (
                <div className="sidebar-nav-children">
                    {resolved.children.map(child => {
                        const isActive = isNavPathActive(pathname, child.path);
                        const badge = badges[child.path] || 0;
                        return (
                            <Link
                                key={child.path}
                                to={child.path}
                                className={`sidebar-nav-item sidebar-nav-child${isActive ? ' is-active' : ''}`}
                                title={child.label}
                            >
                                <i className={child.icon} style={{ fontSize: '0.95rem', width: '1.25rem', textAlign: 'center', flexShrink: 0 }} />
                                <span className="sidebar-nav-label">{child.label}</span>
                                {badge > 0 && (
                                    <span className="sidebar-nav-badge" aria-label={`${badge} pending access requests`}>{badge}</span>
                                )}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function SidebarNav({ sections, pathname, isCollapsed, expandedIds, onToggle, onExpand, collapseOnReclick = true, badges = {} }) {
    return sections.map(section => (
        <NavSection
            key={section.id}
            section={section}
            pathname={pathname}
            isCollapsed={isCollapsed}
            expanded={expandedIds.has(section.id)}
            onToggle={onToggle}
            onExpand={onExpand}
            collapseOnReclick={collapseOnReclick}
            badges={badges}
        />
    ));
}
